// @flow

import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { stopSubmit } from 'redux-form';

import {
  selectCartIsWorking,
  selectCartUUID,
  selectCartReselleeID,
} from '@gandi/cart';
import { CONTACTS_FORM } from '@gandi/domain-configuration';
import { notifyDanger } from '@gandi/notifications';
import { contactbook } from '@gandi/redux-resource-contactbook';
import { withTranslator, type TranslatorProps } from '@gandi/translate';

import {
  SubmissionError,
  withForm,
  FormValidationError,
  selectRegisteredFieldsValues,
} from 'gandi.react-form';
import { withLogger, logging } from 'gandi.redux-logger';

import { withRouter } from 'src/helpers/withRouter';
import type {
  MapDispatchToProps,
  FullState,
  Dispatch,
  GetState,
} from 'src/types';

import { selectSetupOwner } from '../../Setup/modules/selectors';

import WizardPresenter, {
  type Props,
  type OwnProps,
  type StateProps,
  type DispatchProps,
  type ExtraFormDispatchProps,
} from './components/Presenter';

import {
  prepareWizard,
  removeDomain,
  saveStepValues,
  startStepsValidation,
  setTrusteeRoleFlag,
  resetState,
  setInnerStepFlag,
} from './modules/actions';
import {
  selectGlobalErrors,
  selectIsWizardReady,
  selectRawInnerSteps,
  selectRawSteps,
  selectCurrentStep,
  selectCurrentStepErrors,
  selectCurrentStepInitialValues,
  selectUiSteps,
  selectAllStepsAreSuccessful,
  selectDeletingStatus,
  selectWorkingStatus,
  selectValidatingStatus,
  selectTrusteePrices,
  selectTrusteeRoleFlag,
  selectProductsByTypeAndProcessesFactory,
  selectProductsWithDomainContactError,
} from './modules/selectors';

import type { FormValues } from './types';

const selectDomainProductsInCreateTransferOrChangeOwner =
  selectProductsByTypeAndProcessesFactory({
    productType: 'domain',
    processes: ['create', 'transfer', 'change_owner'],
  });

const selectPaasCreateRenew = selectProductsByTypeAndProcessesFactory({
  productType: 'paas',
  processes: ['create', 'renew'],
});

const mapStateToProps = (state: FullState, ownProps: OwnProps): StateProps => {
  const cartId = ownProps.params.cartId;

  return {
    backUrl: `/cart/${cartId}/overview`,
    cartId: selectCartUUID(state),
    domainProducts: selectDomainProductsInCreateTransferOrChangeOwner(state),
    paasCreateRenew: selectPaasCreateRenew(state),
    globalErrors: selectGlobalErrors(state),
    innerSteps: selectRawInnerSteps(state),
    isDeleting: selectDeletingStatus(state),
    isWorking: selectWorkingStatus(state),
    isWizardReady: selectIsWizardReady(state),
    isValidating: selectValidatingStatus(state),
    isCartWorking: selectCartIsWorking(state),
    steps: selectRawSteps(state),
    currentStep: selectCurrentStep(state),
    // $FlowFixMe[incompatible-return]
    currentStepErrors: selectCurrentStepErrors(state),
    uiSteps: selectUiSteps(state),
    trusteePrices: selectTrusteePrices(state),
    allStepsAreSuccessful: selectAllStepsAreSuccessful(state),
  };
};

// create a smooth scroll function to execute at form submission
// move to helpers
const scroll = (el: null | HTMLBodyElement) => {
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

const getContactIdentity = (contact: FormValues) =>
  contact.type !== 'individual'
    ? {
        type: contact.type,
        orgname: contact.orgname,
      }
    : {
        type: contact.type,
        given: contact.given,
        family: contact.family,
      };

/**
 * Validate the form for the current step.
 */
const doSubmit =
  (
    _: FormValues,
    props: Props,
  ): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const trusteeRole = selectTrusteeRoleFlag(getState());
    const initialValues = selectCurrentStepInitialValues(getState());

    const contactValues: FormValues =
      selectRegisteredFieldsValues(getState(), {
        form: CONTACTS_FORM,
      }) || {};

    dispatch(
      saveStepValues(
        props.currentStep,
        // If the user chose a trustee service, we use the initialValues instead of the ones from the form.
        trusteeRole ? { ...initialValues, trustee: true } : contactValues,
      ),
    );

    // Save the extra parameters of the contacts which are favorite contacts.
    let role = props.currentStep?.role;
    let { tld, process } = props.currentStep?.domainInfo || {};

    const contactBookOrgId = selectSetupOwner(getState());
    const hasReselleeId = selectCartReselleeID(getState()) !== '';

    const wizardProducts = selectProductsWithDomainContactError(getState());
    const currentWizardStep = selectCurrentStep(getState());
    const currentWizardProduct = wizardProducts.filter(
      product => product.fqdn === currentWizardStep?.domainInfo.fqdn,
    )[0];

    // $FlowFixMe[incompatible-use]
    const cartPayloadContact = currentWizardProduct[currentWizardStep?.role];
    const contactBookContactId = cartPayloadContact?.id;

    const { extra_parameters: extraParams, ...formValuesContact } =
      contactValues;

    let isSameAsContactBook = false;
    // We save the extra params to the contactbook only if the contact values
    // have kept the same type and name (orgname for PM and firstname + lastname for PP)
    if (contactBookContactId) {
      isSameAsContactBook = isEqual(
        getContactIdentity(formValuesContact),
        getContactIdentity(cartPayloadContact),
      );
    }

    // Do not save extra params when in a reseller buying for a resellee case
    if (contactBookOrgId && extraParams && tld && !hasReselleeId) {
      if (role === 'owner') {
        dispatch(
          contactbook.editOwnerContactExtraParams(
            contactBookOrgId,
            tld,
            role,
            extraParams,
          ),
        );
      } else if (isSameAsContactBook) {
        // If not owner, is obligatory admin, tech or bill, so just check for id
        // presence in the product's payload
        dispatch(
          contactbook.editContactExtraParams(
            contactBookOrgId,
            contactBookContactId,
            tld,
            // $FlowFixMe[incompatible-call] FIXME: if `role` is undefined (cf. props), it will produce an error here
            role,
            extraParams,
          ),
        );
      }
    }

    // continue validation
    return dispatch(startStepsValidation())
      .then(() => {
        if (selectAllStepsAreSuccessful(getState())) {
          props.navigate(props.backUrl);
          return;
        }

        const nextStep = selectCurrentStep(getState());
        tld = nextStep?.domainInfo.tld;
        process = nextStep?.domainInfo.process;
        role = nextStep?.role;

        const purchaseGroupId = nextStep?.purchaseGroupId;
        const roleHasTrustee =
          purchaseGroupId &&
          tld &&
          props.globalErrors[purchaseGroupId]?.[tld]?.trustee_roles.includes(
            role,
          );

        // If trustee service is not yet selected but is required for this role, go back to menu
        if (trusteeRole == null && roleHasTrustee) {
          // Transition 'form' -> 'menu'
          dispatch(
            setInnerStepFlag(
              {
                purchaseGroupId,
                tld,
                process,
                role,
              },
              'menu',
              roleHasTrustee,
            ),
          );
          return;
        }

        // retrieve errors set in the selected step, and set them to the form
        const errors = selectCurrentStepErrors(getState());
        throw new FormValidationError(
          'Error when validation the contact',
          // $FlowFixMe[incompatible-call]
          errors,
        );
      })
      .catch(err => {
        if (err.name === 'FormValidationError') {
          scroll(document.body);
          throw new SubmissionError(err.errors);
        }

        const {
          translator: { __ },
        } = props;

        props.logger.error({ err }, 'Error during contacts validation.');
        dispatch(notifyDanger(__('Unexpected error occurred')));
      });
  };

const initializeWizard =
  (translator: TranslatorProps): (Dispatch => Promise<void>) =>
  async dispatch => {
    const logger = logging(dispatch, 'Wizard');
    try {
      await dispatch(prepareWizard());
      await dispatch(startStepsValidation());
    } catch (err) {
      logger.error({ err }, 'Error during contacts validation.');
      dispatch(notifyDanger(translator.__('Unexpected error occurred')));
    }
  };

const onSubmit = (
  contactValues: FormValues,
  dispatch: Dispatch,
  props: Props,
): any => dispatch(doSubmit(contactValues, props));

const mapDispatchToProps: MapDispatchToProps<DispatchProps> = {
  initializeWizard,
  saveStepValues,
  startStepsValidation,
  notifyDanger,
  resetState,
  removeDomain,
  setTrusteeRoleFlag,
  stopSubmit,
  setInnerStepFlag,
};

const mapFormProps = (state: FullState) => {
  const stepValues = selectCurrentStep(state)?.values;

  return {
    initialValues: isEmpty(stepValues)
      ? selectCurrentStepInitialValues(state)
      : stepValues,
  };
};

const mapExtraFormDispatchProps: MapDispatchToProps<ExtraFormDispatchProps> = {
  stopSubmit,
};

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  withLogger('Wizard/Presenter'),
  // required by `doSubmit`
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  // Redux-form initialization props.
  connect(mapFormProps),
  withForm({
    form: CONTACTS_FORM,
    propNamespace: 'form',
    onSubmit,
  }),
  connect(null, mapExtraFormDispatchProps),
)(WizardPresenter): React.ComponentType<{}>);
